import { lt } from 'lodash';

import { ILegacyCartEntry, ISanityImage } from '@rbi-ctg/menu';
import {
  EntryTypeEnum,
  IConfigOfferEntryDto,
  IEntryReferenceDto,
  IMenuEntryDto,
  IPriceDto,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
  ServiceMode,
} from 'generated/graphql-gateway';
import { LoyaltyReward } from 'state/loyalty/types';
import { isOfferType } from 'state/order/utils';
import { CartEntryType } from 'utils/cart/types';

import {
  ILocalizedMenu,
  ILocalizedMenuEntry,
  IMainMenuNode,
  IndexedMenu,
  MenuItemType,
  PosServiceMode,
} from './types';

export function createIndexedMenu(menu: ILocalizedMenu): IndexedMenu<ILocalizedMenuEntry> {
  const map = new Map<string, ILocalizedMenuEntry>();

  menu.entries.forEach(entry => map.set(entry.id, entry));

  // TODO: Remove this once we have a proper way to handle offers and rewards.
  // Note that this will be a really complex thing to achieve
  Object.entries(
    mapOffersToMenuEntryDto(map, menu.systemWideOffers, menu.configOffers)
  ).forEach(([id, value]) => map.set(id, value));

  Object.entries(mapRewardsToMenuEntryDto(map, menu.rewards)).forEach(([id, value]) => {
    map.set(id, value);
  });

  const indexedMenu = new IndexedMenu(map);
  return indexedMenu;
}

export function createIndexedOffers(
  menu: ILocalizedMenu
): IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto> {
  const map = new Map<string, IConfigOfferEntryDto | ISystemWideOfferEntryDto>();
  Object.entries(menu.configOffers).forEach(([_, value]) => map.set(value.id, value));
  Object.entries(menu.systemWideOffers).forEach(([_, value]) => map.set(value.id, value));
  return new IndexedMenu(map);
}

export function createIndexedRewards(menu: ILocalizedMenu): IndexedMenu<IRewardEntryDto> {
  const map = new Map<string, IRewardEntryDto>();
  Object.entries(menu?.rewards || {}).forEach(([_, value]) => map.set(value.id, value));
  return new IndexedMenu(map);
}

export interface IMenuQueryOptions {
  posServiceMode: PosServiceMode;
}

export interface ICreateMainMenuOptions extends IMenuQueryOptions {
  dayPartIds: string[];
}

/**
 * Creates the main menu hierarchy of sections and products
 * Menu
 *  ├── Meals                     (Section)
 *  │   └── Whopper Meals         (Product)
 *  ├── Flame Grilled Burgers     (Section)
 *  │   └── Double Whopper        (Product)
 *  ├── Chicken & Fish            (Section)
 *  │   └── Royal Crispy Chicken   (Product)
 * @param menu The menu to create the hierarchy from
 * @param rootId The id of the root menu entry
 * @param options Options for creating the main menu
 * @returns The main menu hierarchy
 */
export function createMainMenuNode(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  rootId: string,
  options: ICreateMainMenuOptions
): IMainMenuNode | undefined {
  const entry = menu.get(rootId);
  if (!entry) {
    return undefined;
  }

  if (!isEntryAvailable(entry, options.posServiceMode, options.dayPartIds)) {
    return undefined;
  }

  const node: IMainMenuNode = {
    id: entry.id,
    type: getMainMenuNodeType(entry.type),
    name: entry.name?.locale ?? entry.id,
    image: {
      resource: mapImageResourceToSanityImage(entry.image?.resource ?? ''),
      description: entry.image?.altText?.locale ?? '',
    },
    children: [],
    product: entry.price
      ? {
          price: getEntryPriceOrZero(entry, options.posServiceMode),
          calories: entry.nutrition?.calories?.def ?? entry.nutrition?.calories?.min ?? 0,
        }
      : undefined,
  };

  if (entry.type === EntryTypeEnum.SECTION || entry.type === EntryTypeEnum.MENU) {
    entry.options?.entries?.forEach(option => {
      const child = createMainMenuNode(menu, option.entryId, options);
      if (child) {
        node.children.push(child);
      }
    });

    // If there are no entities in the section, don't include it
    if (node.children.length === 0) {
      return undefined;
    }
  }

  return node;
}

export function getPosServiceMode(serviceMode: ServiceMode): PosServiceMode {
  return serviceMode === ServiceMode.DELIVERY || serviceMode === ServiceMode.CATERING_DELIVERY
    ? 'delivery'
    : 'pickup';
}

export function getAvailableOptions(
  entryReferences: IEntryReferenceDto[] | undefined | null | Readonly<IEntryReferenceDto[]>,
  posServiceMode: PosServiceMode,
  dayPartIds: string[] | undefined
): IEntryReferenceDto[] {
  return entryReferences?.filter(ref => isEntryAvailable(ref, posServiceMode, dayPartIds)) ?? [];
}

/**
 * Returns the available options for a menu entry, optionally mapping and filtering them to a different type.
 * The map function can return undefined to exclude items.
 * @param menu Indexed menu to look up related entries
 * @param posServiceMode POS service mode to check availability
 * @param entryReferences References to entries to map
 * @param mapFn Function to map entry references to a different type
 * @returns Available options mapped and filtered by the map function
 */
export function mapAvailableOptions<T>(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  posServiceMode: PosServiceMode,
  entryReferences: IEntryReferenceDto[] | undefined | null | Readonly<IEntryReferenceDto[]>,
  mapFn: (entryReference: IEntryReferenceDto) => T | undefined
): T[] {
  if (!entryReferences) {
    return [];
  }

  const result: T[] = [];
  for (const ref of entryReferences) {
    if (!isEntryAvailable(ref, posServiceMode, undefined)) {
      continue;
    }

    const entry = menu.get(ref.entryId);
    if (!isEntryAvailable(entry, posServiceMode, undefined)) {
      continue;
    }

    const mapped = mapFn(ref);
    if (mapped) {
      result.push(mapped);
    }
  }
  return result;
}

export function isEntryAvailable(
  entry: ILocalizedMenuEntry | IEntryReferenceDto,
  posServiceMode: PosServiceMode,
  dayPartIds?: string[]
): boolean {
  // Day part check. Assumes no dayparts means it's available all day
  if (
    'dayParts' in entry &&
    entry.dayParts &&
    dayPartIds &&
    !dayPartIds.some(n => entry.dayParts!.includes(n))
  ) {
    return false;
  }

  // Entry availability check. Assumes lack of availability means it's available
  if (entry.availability && entry.availability[posServiceMode] === false) {
    return false;
  }

  return true;
}

export function getPickerProduct(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  picker: ILocalizedMenuEntry,
  selections: Record<string, string>,
  posServiceMode: PosServiceMode,
  pickerAspectKey?: string
) {
  if (picker.type !== EntryTypeEnum.PICKER) {
    throw new Error('Entry is not a picker');
  }

  const pickerAspectId = pickerAspectKey ?? getFirstDefaultId(picker);
  const selectedId = selections[pickerAspectId];
  const result = findCheapestAvailableProduct(
    menu,
    selectedId ?? pickerAspectId,
    posServiceMode,
    selectedId
  );

  if (!result) {
    throw new Error(`No available product found under picker "${picker.id}"`);
  }

  return result;
}

/**
 * Searches for the cheapest available item or combo by always diving default-first
 * and then backtracking to the other siblings.
 * Forces the return of the product selected by the user if applicable.
 */
export function findCheapestAvailableProduct(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  entryId: string,
  posServiceMode: PosServiceMode,
  selectedId?: string,
  cheapest?: ILocalizedMenuEntry
): ILocalizedMenuEntry | undefined {
  const entry = menu.get(entryId);

  // Leaf check
  if (entryId !== cheapest?.id && isProductAvailable(entry, posServiceMode)) {
    if (selectedId) {
      return entry;
    }
    if (cheapest && entry.type !== cheapest.type) {
      return cheapest; // avoid deep search in modifiers
    }

    const entryPrice = getEntryPriceOrZero(entry, posServiceMode);
    const cheapestPrice = (cheapest && getEntryPrice(cheapest, posServiceMode)) ?? Number.MAX_VALUE;

    if (lt(entryPrice, cheapestPrice)) {
      cheapest = entry;
    }

    return cheapest ?? entry;
  }

  // Skip the entire subtree if the "Aspect" is not available
  if (isAspectOption(entry) && !isEntryAvailable(entry, posServiceMode)) {
    return undefined;
  }

  if (entry.options?.entries?.length) {
    const defaultOptionId = getFirstDefaultId(entry);
    const defaultOption = findCheapestAvailableProduct(
      menu,
      defaultOptionId,
      posServiceMode,
      selectedId,
      cheapest
    );

    if (defaultOption) {
      return defaultOption;
    }

    // Backtrack: try the other siblings
    for (const { entryId: optionId } of entry.options.entries) {
      if (optionId === defaultOptionId) {
        continue;
      }
      const found = findCheapestAvailableProduct(
        menu,
        optionId,
        posServiceMode,
        selectedId,
        cheapest
      );
      if (found) {
        cheapest = found;
      }
    }
  }

  return cheapest;
}

export function getComboSlotAvailableEntries({
  entry,
  menu,
  posServiceMode,
  dayPartIds,
}: {
  entry: ILocalizedMenuEntry;
  menu: IndexedMenu<ILocalizedMenuEntry>;
  posServiceMode: PosServiceMode;
  dayPartIds?: string[];
}): IEntryReferenceDto[] {
  return (
    entry.options?.entries?.filter(entryOption => {
      const menuEntry = menu.find(entryOption.entryId);
      if (!menuEntry) {
        return false;
      }
      return isEntryAvailable(menuEntry, posServiceMode, dayPartIds);
    }) ?? []
  );
}

export function isProductAvailable(
  entry: ILocalizedMenuEntry,
  posServiceMode: PosServiceMode
): boolean {
  return (isItem(entry) || isCombo(entry)) && isEntryAvailable(entry, posServiceMode);
}

export function isCombo(entry: ILocalizedMenuEntry | string): boolean {
  return (
    normalizeType(typeof entry === 'string' ? entry : entry.type) ===
    normalizeType(EntryTypeEnum.COMBO)
  );
}

export function isComboSlot(entry: ILocalizedMenuEntry | string): boolean {
  return (
    normalizeType(typeof entry === 'string' ? entry : entry.type) ===
    normalizeType(EntryTypeEnum.COMBO_SLOT)
  );
}

export function isSlotOption(entry: ILocalizedMenuEntry): boolean {
  return normalizeType(entry.type) === normalizeType(EntryTypeEnum.SLOT_OPTION);
}

export function isItem(entry: ILocalizedMenuEntry): boolean {
  return entry.type === EntryTypeEnum.ITEM;
}

export function isModifier(entry: ILocalizedMenuEntry): boolean {
  return entry.type === EntryTypeEnum.MODIFIER;
}

export function isPicker(entry: ILocalizedMenuEntry): boolean {
  return entry.type === EntryTypeEnum.PICKER;
}

export function isPickerAspect(entry: ILocalizedMenuEntry): boolean {
  return entry.type === EntryTypeEnum.PICKER_ASPECT;
}

export function isAspectOption(entry: ILocalizedMenuEntry): boolean {
  return entry.type === EntryTypeEnum.ASPECT_OPTION;
}

export const normalizeType = (type?: string) => type?.replace(/[\W_]+/g, '').toUpperCase();

export function hasOptionEntries(
  posServiceMode: PosServiceMode,
  entry: ILocalizedMenuEntry
): entry is ILocalizedMenuEntry & { options: { entries: IEntryReferenceDto[] } } {
  if (!entry.options?.entries?.length) {
    return false;
  }

  for (const option of entry.options.entries) {
    if (isEntryAvailable(option, posServiceMode)) {
      return true;
    }
  }

  return false;
}

/**
 * Returns value from possible null chain or throws
 * @example
 * const value = unwrap(obj?.prop?.value, 'Value is required');
 */
export function unwrap<T>(value: T | undefined | null, error: string): T {
  if (value !== undefined && value !== null) {
    return value;
  }
  throw new Error(error);
}

export function mapImageResourceToSanityImage(resource: string, altText?: string): ISanityImage {
  return {
    locale: altText && {
      imageDescription: altText,
    },
    asset: {
      _id: resource,
    },
  } as ISanityImage;
}

export function stringifySanityImage(
  image: ILocalizedMenuEntry['image'] | undefined
): string | undefined {
  return image?.resource
    ? JSON.stringify(mapImageResourceToSanityImage(image.resource))
    : undefined;
}

export const getMainMenuNodeType = (type: EntryTypeEnum): MenuItemType => {
  switch (type) {
    case EntryTypeEnum.PICKER:
      return EntryTypeEnum.PICKER;
    case EntryTypeEnum.SECTION:
      return EntryTypeEnum.SECTION;
    case EntryTypeEnum.COMBO:
      return EntryTypeEnum.COMBO;
    default:
      return EntryTypeEnum.ITEM;
  }
};

/**
 * Gets the first default id for a menu entry
 */
export function getFirstDefaultId(
  entry: ILocalizedMenuEntry,
  fallbackToFirstOption: boolean = true
): string {
  let defaultId = entry.options?.defaults?.[0];

  if (!defaultId && fallbackToFirstOption) {
    defaultId = entry.options?.entries?.[0]?.entryId;
  }

  if (!defaultId) {
    throw new Error(`No default found for ${entry.id}`);
  }

  return defaultId;
}

/**
 * Gets the first option id for a menu entry or throws if none found
 */
export function getFirstOption(entry: ILocalizedMenuEntry): IEntryReferenceDto {
  const firstOption = entry.options?.entries?.[0];
  if (!firstOption) {
    throw new Error(`No options found for ${entry.id}`);
  }
  return firstOption;
}

function getOptionEntries(
  parent: ILocalizedMenuEntry | undefined,
  entriesMap: Map<string, ILocalizedMenuEntry> | IndexedMenu<ILocalizedMenuEntry>
): ILocalizedMenuEntry[] {
  const refs = parent?.options?.entries ?? [];
  const children: ILocalizedMenuEntry[] = [];
  for (const ref of refs) {
    // If entriesMap is an IndexedMenu, use .find; if it's a Map, use .get
    let child: ILocalizedMenuEntry | undefined;
    if (typeof (entriesMap as any).find === 'function') {
      child = (entriesMap as IndexedMenu<ILocalizedMenuEntry>).find(ref.entryId);
    } else {
      child = (entriesMap as Map<string, ILocalizedMenuEntry>).get(ref.entryId);
    }
    if (child) {
      children.push(child);
    }
  }
  return children;
}

/**
 * Returns the price for a menu entry in a given service mode and context or zero if no price is found
 * @param entry - entry to price
 * @param posServiceMode - service mode to get the price for
 * @param contextualPrice - contextual price to use if available. Ex: combo price.
 * @returns price for the entry
 */
export function getEntryPriceOrZero(
  entry: ILocalizedMenuEntry,
  posServiceMode: PosServiceMode,
  contextualPrice?: IPriceDto
): number {
  return contextualPrice?.[posServiceMode] ?? entry.price?.[posServiceMode] ?? 0;
}

/**
 * Returns the price for a menu entry in a given service mode or undefined if no price is found
 * @param entry - entry to price
 * @param posServiceMode - service mode to get the price for
 * @param contextualPrice - contextual price to use if available. Ex: combo price.
 * @returns price for the entry
 */
export function getEntryPrice(
  entry: ILocalizedMenuEntry,
  posServiceMode: PosServiceMode,
  contextualPrice?: IPriceDto
): number | undefined {
  return contextualPrice?.[posServiceMode] ?? entry.price?.[posServiceMode] ?? undefined;
}

/**
 * Returns the slot option for a given item id in a combo slot.
 * @param indexedMenu - indexed menu to look up related entries
 * @param comboSlotId - combo slot id to look up
 * @param itemId - item id to look up
 * @returns slot option for the item in the combo slot
 */
export function getSlotOptionByItemId(
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>,
  comboSlotId: string,
  itemId: string
): ILocalizedMenuEntry {
  const comboSlot = indexedMenu.get(comboSlotId, { type: EntryTypeEnum.COMBO_SLOT });
  for (const slotOptionRef of comboSlot.options?.entries ?? []) {
    const slotOption = indexedMenu.get(slotOptionRef.entryId, { type: EntryTypeEnum.SLOT_OPTION });
    // a SLOT_OPTION has a single option which is an ITEM
    const itemRef = getFirstOption(slotOption);
    if (itemRef.entryId === itemId) {
      return slotOption;
    }
  }

  throw new Error(`Slot option for item ${itemId} not found in combo slot ${comboSlotId}`);
}

export function getComboSlotDefaultItem(
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>,
  comboSlotId: string
): ILocalizedMenuEntry {
  const comboSlot = indexedMenu.get(comboSlotId, { type: EntryTypeEnum.COMBO_SLOT });
  const defaultItemId = getFirstDefaultId(comboSlot);
  return indexedMenu.get(defaultItemId, { type: EntryTypeEnum.ITEM });
}

/**
 * Returns the price for an item in a combo slot taking
 * into account contextual pricing.
 * @param indexedMenu - indexed menu to look up related entries
 * @param comboSlotId - combo slot id to look up
 * @param itemId - item id to look up otherwise combo slot default is used
 * @returns slot option for the item id or default in the combo slot
 */
export function getComboSlotItemPrice(
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>,
  comboSlotId: string,
  itemId: string | undefined
): IPriceDto | undefined {
  const comboSlot = indexedMenu.get(comboSlotId, { type: EntryTypeEnum.COMBO_SLOT });

  let itemRef = itemId
    ? comboSlot.options?.entries?.find(n => n.entryId === itemId)
    : getFirstOption(comboSlot);

  itemRef = itemRef ?? getFirstOption(comboSlot);

  if (itemRef.price) {
    return itemRef.price;
  }

  const item = indexedMenu.get(itemRef.entryId);
  return item.price ?? undefined;
}

export type Typenameless<T> = Omit<T, '__typename'>;

export function getKeysWithoutTypename<T extends {}>(object: T): (keyof Typenameless<T>)[] {
  var keys = Object.keys(object).filter(key => key !== '__typename');
  return keys as any;
}

function emptyEntryReference(entryId: string): IEntryReferenceDto {
  return {
    entryId,
    price: {
      __typename: 'PriceDto',
      pickup: 0,
      delivery: 0,
    },
    __typename: 'EntryReferenceDto',
  };
}

/**
 * Builds a map of sanityId -> menu entry adding the offer id and price to the
 * incentive combo / picker item found in the menu
 * @param entries - menu entries
 * @param systemWideOffers - system wide offers
 * @param configOffers - config offers
 * @returns a map of sanityId -> menu entry
 */
function mapOffersToMenuEntryDto(
  entriesMap: Map<string, ILocalizedMenuEntry>,
  systemWideOffers: ILocalizedMenu['systemWideOffers'],
  configOffers: ILocalizedMenu['configOffers']
): Record<string, IMenuEntryDto> {
  const mappedEntities: Record<string, ILocalizedMenuEntry> = {};
  const allEntities = [...(systemWideOffers ?? []), ...(configOffers ?? [])];

  for (const entity of allEntities) {
    const menuReferenceId = entity.options?.[0]?.entryId;
    if (!menuReferenceId) {
      continue;
    }

    const menuEntry = entriesMap.get(menuReferenceId);
    if (!menuEntry) {
      // this maps free items, and discounts offers to menu entries
      // note that the absence of day parts means it's available for all day parts
      mappedEntities[entity.id] = {
        // We use the offer id as the id for the menu entry
        id: entity.id,
        // We use the offer price as the price for the menu entry
        price: entity.price,
        type: EntryTypeEnum.ITEM,
        __typename: 'MenuEntryDto',
        // a reference to the loyalty engine id
        loyaltyEngineIdReference: entity.loyaltyEngineId,
        // instead of not adding the item, we will use this field to mark it as unavailable
        availability: entity.availability,
      };
      continue;
    }

    // If the menu entry is a picker, we need to update the price of the combo entries under the picker
    if (menuEntry.type === EntryTypeEnum.PICKER) {
      const entriesUnderPicker = getOptionEntries(menuEntry, entriesMap)
        .flatMap(pickerAspect => getOptionEntries(pickerAspect, entriesMap))
        .flatMap(pickerAspectOption => getOptionEntries(pickerAspectOption, entriesMap));

      for (const entryToUpdate of entriesUnderPicker) {
        entriesMap.set(entryToUpdate.id, {
          ...entryToUpdate,
          price: entity.price,
        });
      }
    }

    // this maps combo, and picker offers to menu entries
    mappedEntities[entity.id] = {
      ...menuEntry,
      // We use the offer id as the id for the menu entry
      id: entity.id,
      // We use the offer price as the price for the menu entry
      price: entity.price,
      // a reference to the loyalty engine id
      loyaltyEngineIdReference: entity.loyaltyEngineId,
      availability: entity.availability,
    };
  }
  return mappedEntities;
}

function mapRewardsToMenuEntryDto(
  menu: Map<string, ILocalizedMenuEntry>,
  rewards: ILocalizedMenu['rewards']
): Record<string, ILocalizedMenuEntry> {
  const mappedEntities: Record<string, ILocalizedMenuEntry> = {}; // combo slot id -> menu entry

  // Maps rewards using new structure
  for (const loyaltyEntity of rewards ?? []) {
    const rewardBenefitRef = loyaltyEntity.options?.[0];
    const rewardBenefit = menu.get(rewardBenefitRef?.entryId);

    if (rewardBenefit) {
      const rewardBenefitEntry = {
        ...rewardBenefit,
        id: loyaltyEntity.id,
        rewardIdReference: loyaltyEntity.id,
      };

      mappedEntities[loyaltyEntity.id] = rewardBenefitEntry;

      continue;
    }

    const comboSlotIds: string[] = [];

    (loyaltyEntity.options ?? []).forEach((option, index) => {
      const manuallyCreatedComboSlotId = `${loyaltyEntity.id}_comboSlot_${index}`;

      // Maps the reward id to the menu item reference
      const menuItemReference = menu.get(option.entryId);
      if (menuItemReference) {
        menuItemReference.rewardIdReference = loyaltyEntity.id;
      }

      mappedEntities[manuallyCreatedComboSlotId] = {
        id: manuallyCreatedComboSlotId,
        options: {
          entries: [emptyEntryReference(option.entryId)],
        },
        type: EntryTypeEnum.COMBO_SLOT,
        __typename: 'MenuEntryDto',
      } as ILocalizedMenuEntry;
      comboSlotIds.push(manuallyCreatedComboSlotId);
    });

    mappedEntities[loyaltyEntity.id] = {
      ...loyaltyEntity,
      id: loyaltyEntity.id,
      options: {
        entries: comboSlotIds.map(emptyEntryReference),
      },
      type: EntryTypeEnum.COMBO,
      __typename: 'MenuEntryDto',
    } as ILocalizedMenuEntry;
  }

  return mappedEntities;
}

export const getRewardBenefit = reward => {};
/**
 * Enriches a legacy cart entry by adding missing additional data from one menu ( mostly for images ).
 * This is intended to be used when we miss menu data information for the cartEntry, mostly for reordering from menu recents.
 *
 * This function recursively enriches a cart entry and its children by:
 * - Looking for menu data in the provided indexed menu using `sanityId` or `_id`.
 * - If the menu data has an image resource, mapping it to a Sanity image format and adding it to the cart entry.
 * - Recursively enriching all child cart entries in the same way.
 *
 * @param {ILegacyCartEntry} cartEntry - The cart entry to enrich.
 * @param {IndexedMenu} indexedMenu - One Menu data.
 *
 * @returns {ILegacyCartEntry} The enriched cart entry with updated image data and enriched children.
 */
export const enrichLegacyCartEntryFromMenu = (
  cartEntry: ILegacyCartEntry,
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>,
  allRewardsMap: Record<string, LoyaltyReward>
): ILegacyCartEntry => {
  let enrichedCartEntry = { ...cartEntry };

  const cartEntryTypes = [CartEntryType.item, CartEntryType.combo, CartEntryType.offerCombo];

  const isOfferCombo = isOfferType(cartEntry.type);
  const reward = allRewardsMap?.[cartEntry.sanityId];

  const rewardBenefitId =
    // For one menu picker rewards we should check within the picker options to get the benefit
    reward?.incentives[0]?._type === 'picker'
      ? // @ts-ignore
        reward?.incentives[0]?.options?.find(
          // Check for the first children in the cart entry to get the benefit id.
          // All the three children a reward entry structure has, they all contain the benefit id.
          option => option.option._id === cartEntry.children[0]._id
        )?.option._id
      : reward?.incentives[0]?._id;

  const id = isOfferCombo && !!reward ? rewardBenefitId : cartEntry.sanityId || cartEntry._id;

  if (cartEntryTypes.includes(cartEntry.type)) {
    const menuData = indexedMenu.find(id);

    if (menuData?.image?.resource) {
      const image = mapImageResourceToSanityImage(
        menuData.image.resource,
        menuData.image.altText?.locale ?? ''
      );

      enrichedCartEntry = {
        ...cartEntry,
        image,
      };
    }
  }

  const enrichedChildren = cartEntry.children?.map(child =>
    enrichLegacyCartEntryFromMenu(child, indexedMenu, allRewardsMap)
  );

  return {
    ...enrichedCartEntry,
    children: enrichedChildren ?? [],
  };
};
